import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import BarcodeDisplay from './BarcodeDisplay';
import { celebratePickup } from '../utils/confetti';
import soundEffects from '../utils/sounds';
import adminApi from '../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackageList = ({ packages, onPackageUpdated, onPackageDeleted }) => {
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [expandedPackages, setExpandedPackages] = useState({}); // Track which packages are expanded
  
  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editForm, setEditForm] = useState({
    recipient_name: '',
    tracking_numbers: [],
    carrier: '',
    phone: '',
    email: '',
    notes: ''
  });
  
  // Ref for edit modal
  const editModalRef = useRef(null);

  const handleViewImage = (pkg) => {
    setSelectedPackage(pkg);
    setSelectedImage(pkg.captured_image);
    setShowImageModal(true);
  };

  const togglePackageExpand = (packageId) => {
    setExpandedPackages(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };
  const handlePickup = async (packageId, packageInfo) => {
    try {
      const response = await adminApi.patch(`${API}/packages/${packageId}`, {
        is_picked_up: true
      });
      onPackageUpdated(response.data);
      celebratePickup();
      soundEffects.playSuccess();
      toast.success(`Package #${packageInfo.box_number} marked as picked up`);
    } catch (error) {
      console.error('Error marking package as picked up:', error);
      toast.error('Failed to mark package as picked up');
    }
  };

  const handleDelete = async (packageId, packageInfo) => {
    if (!window.confirm(`Are you sure you want to delete package #${packageInfo.box_number} for ${packageInfo.recipient_name}?`)) {
      return;
    }

    try {
      const response = await adminApi.delete(`${API}/packages/${packageId}`);
      soundEffects.playPop();
      onPackageDeleted(packageId);
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error('Failed to delete package');
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setEditForm({
      recipient_name: pkg.recipient_name || '',
      tracking_numbers: pkg.tracking_numbers || [],
      carrier: pkg.carrier || '',
      phone: pkg.phone || '',
      email: pkg.email || '',
      notes: pkg.notes || ''
    });
    setShowEditModal(true);
    
    // Scroll to make sure modal is visible
    setTimeout(() => {
      if (editModalRef.current) {
        editModalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleEditSubmit = async () => {
    try {
      // Format tracking numbers (convert array to ensure proper format)
      const updateData = {
        recipient_name: editForm.recipient_name,
        tracking_numbers: editForm.tracking_numbers,
        carrier: editForm.carrier,
        phone: editForm.phone,
        email: editForm.email,
        notes: editForm.notes
      };

      const response = await adminApi.patch(`${API}/packages/${editingPackage.id}`, updateData);
      onPackageUpdated(response.data);
      toast.success('Package updated successfully!');
      setShowEditModal(false);
      setEditingPackage(null);
    } catch (error) {
      console.error('Error updating package:', error);
      toast.error('Failed to update package');
    }
  };

  const handleTrackingNumberChange = (index, value) => {
    const newTrackingNumbers = [...editForm.tracking_numbers];
    newTrackingNumbers[index] = value;
    setEditForm({ ...editForm, tracking_numbers: newTrackingNumbers });
  };

  const addTrackingNumber = () => {
    setEditForm({ ...editForm, tracking_numbers: [...editForm.tracking_numbers, ''] });
  };

  const removeTrackingNumber = (index) => {
    const newTrackingNumbers = editForm.tracking_numbers.filter((_, i) => i !== index);
    setEditForm({ ...editForm, tracking_numbers: newTrackingNumbers });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to calculate package age in days
  const getPackageAgeDays = (dateAdded) => {
    const now = new Date();
    const added = new Date(dateAdded);
    const diffTime = Math.abs(now - added);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get age badge color and text (Updated for 2-week policy)
  const getAgeBadge = (dateAdded) => {
    const days = getPackageAgeDays(dateAdded);
    
    if (days <= 7) {
      return { color: 'bg-green-100 text-green-700', icon: '🟢', text: `${days}d ago` };
    } else if (days <= 11) {
      return { color: 'bg-yellow-100 text-yellow-700', icon: '🟡', text: `${days}d ago` };
    } else {
      return { color: 'bg-red-100 text-red-700', icon: '🔴', text: `${days}d ago - URGENT` };
    }
  };

  if (packages.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-inbox text-gray-300 text-6xl mb-4"></i>
        <p className="text-gray-500 text-lg">No packages to display</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 overflow-x-hidden">
      {packages.map((pkg, index) => {
        const isExpanded = expandedPackages[pkg.id];
        
        return (
          <div 
            key={pkg.id} 
            className={`package-item ${pkg.is_picked_up ? 'picked-up' : ''} rounded-lg overflow-hidden shadow-sm border border-gray-200 transition-all duration-300 hover:shadow-md`}
            style={{ animation: `fadeSlideUp 0.4s ease-out forwards`, animationDelay: `${Math.min(index * 50, 500)}ms`, opacity: 0 }}
            data-testid={`package-${pkg.box_number}`}
          >
            {/* COLLAPSED VIEW - Name, Tracking, Address */}
            <div 
              className="flex items-start justify-between p-4 cursor-pointer hover:bg-blue-50 transition-all active:bg-blue-100 select-none relative z-10"
              onClick={(e) => {
                console.log('Clicked package:', pkg.box_number);
                togglePackageExpand(pkg.id);
              }}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  togglePackageExpand(pkg.id);
                }
              }}
              style={{ userSelect: 'none' }}
            >
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className={`w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${
                  pkg.is_picked_up ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-600'
                }`}>
                  #{pkg.box_number}
                </div>
                
                <div className="flex-1 min-w-0 space-y-1">
                  {/* Name */}
                  <div className="flex items-center space-x-2 flex-wrap">
                    <h3 className="font-bold text-gray-800 text-base break-words">{pkg.recipient_name}</h3>
                    {pkg.is_picked_up && (
                      <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium whitespace-nowrap">
                        <i className="fas fa-check-circle mr-1"></i>
                        Picked Up
                      </span>
                    )}
                    {/* Age Badge */}
                    {!pkg.is_picked_up && pkg.date_added && (
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap ${getAgeBadge(pkg.date_added).color}`}>
                        <span className="mr-1">{getAgeBadge(pkg.date_added).icon}</span>
                        {getAgeBadge(pkg.date_added).text}
                      </span>
                    )}
                  </div>
                  
                  {/* Tracking Number */}
                  {(pkg.tracking_number || (pkg.tracking_numbers && pkg.tracking_numbers[0])) && (
                    <div className="flex items-center space-x-2 overflow-hidden">
                      <i className="fas fa-barcode text-blue-600 text-xs flex-shrink-0"></i>
                      <span className="font-mono text-blue-800 font-semibold text-sm truncate">
                        {pkg.tracking_number || pkg.tracking_numbers[0]}
                      </span>
                    </div>
                  )}
                  
                  {/* Address */}
                  {pkg.ocr_data?.addresses && pkg.ocr_data.addresses.length > 0 && (
                    <div className="flex items-start space-x-2 overflow-hidden">
                      <i className="fas fa-map-marker-alt text-green-600 text-xs flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700 text-sm truncate">
                        {pkg.ocr_data.addresses[0]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Expand/Collapse Icon */}
              <div className="flex items-center flex-shrink-0 ml-2 pointer-events-none">
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-base text-gray-400`}></i>
              </div>
            </div>

            {/* EXPANDED VIEW - Full Details */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-2 border-t border-gray-200 bg-gray-50" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-4">
                  
                  {/* Additional Tracking Numbers */}
                  {pkg.tracking_numbers && pkg.tracking_numbers.length > 1 && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="font-semibold text-purple-800 text-sm mb-2">
                        <i className="fas fa-list mr-2"></i>
                        Additional Tracking Numbers
                      </div>
                      {pkg.tracking_numbers.slice(1).map((tracking, idx) => (
                        <div key={idx} className="font-mono text-sm text-purple-900 py-1">
                          • {tracking}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Barcode Display */}
                  {(pkg.tracking_number || (pkg.tracking_numbers && pkg.tracking_numbers[0])) && (
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <div className="font-semibold text-gray-700 text-sm mb-2 text-center">
                        <i className="fas fa-barcode mr-2"></i>
                        Scannable Barcode
                      </div>
                      <BarcodeDisplay 
                        trackingNumber={pkg.tracking_number || pkg.tracking_numbers[0]}
                        carrier={pkg.carrier}
                        size="small"
                        showNumber={true}
                      />
                    </div>
                  )}

                  {/* Phone Numbers from OCR */}
                  {pkg.ocr_data?.phone_numbers && pkg.ocr_data.phone_numbers.length > 0 && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="font-semibold text-green-800 text-sm mb-2">
                        <i className="fas fa-phone mr-2"></i>
                        Phone Numbers
                      </div>
                      {pkg.ocr_data.phone_numbers.map((phone, idx) => (
                        <div key={idx} className="text-sm text-green-900">• {phone}</div>
                      ))}
                    </div>
                  )}

                  {/* Additional Information - OCR Data, Addresses, Emails */}
                  {pkg.ocr_data && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="font-semibold text-blue-800 text-sm mb-2">
                        <i className="fas fa-info-circle mr-2"></i>
                        Additional Information
                      </div>
                      
                      {/* OCR Confidence Score */}
                      {pkg.ocr_data.confidence_score !== undefined && (
                        <div className="mb-2 flex items-center space-x-2">
                          <strong className="text-sm text-blue-900">OCR Confidence:</strong>
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full w-24">
                              <div 
                                className={`h-2 rounded-full ${
                                  pkg.ocr_data.confidence_score >= 0.8 ? 'bg-green-500' : 
                                  pkg.ocr_data.confidence_score >= 0.6 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${pkg.ocr_data.confidence_score * 100}%` }}
                              ></div>
                            </div>
                            <span className={`text-xs font-bold ${
                              pkg.ocr_data.confidence_score >= 0.8 ? 'text-green-700' : 
                              pkg.ocr_data.confidence_score >= 0.6 ? 'text-yellow-700' : 'text-red-700'
                            }`}>
                              {(pkg.ocr_data.confidence_score * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Emails */}
                      {pkg.ocr_data.emails && pkg.ocr_data.emails.length > 0 && (
                        <div className="text-sm text-blue-900 mb-2">
                          <strong>Email:</strong> {pkg.ocr_data.emails.join(', ')}
                        </div>
                      )}
                      
                      {/* Addresses */}
                      {pkg.ocr_data.addresses && pkg.ocr_data.addresses.length > 0 && (
                        <div className="text-sm text-blue-900 mb-2">
                          <strong>Address:</strong> {pkg.ocr_data.addresses.join(', ')}
                        </div>
                      )}
                      
                      {/* Carrier */}
                      {pkg.carrier && (
                        <div className="text-sm text-blue-900 mb-2">
                          <strong>Carrier:</strong> {pkg.carrier}
                        </div>
                      )}
                      
                      {/* Raw Text Preview */}
                      {pkg.ocr_data.raw_text_lines && pkg.ocr_data.raw_text_lines.length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-blue-700 cursor-pointer hover:text-blue-900">
                            View Raw OCR Text ({pkg.ocr_data.raw_text_lines.length} lines)
                          </summary>
                          <div className="mt-2 text-xs text-gray-600 max-h-32 overflow-y-auto bg-white p-2 rounded">
                            {pkg.ocr_data.raw_text_lines.slice(0, 20).join(' | ')}
                          </div>
                        </details>
                      )}
                    </div>
                  )}

                  {/* Complete Raw Data from OCR Scan */}
                  {pkg.raw_data && (
                    <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="font-semibold text-yellow-800 text-sm mb-2">
                        <i className="fas fa-file-alt mr-2"></i>
                        Complete Raw OCR Data
                      </div>
                      <details className="mt-2">
                        <summary className="text-xs text-yellow-700 cursor-pointer hover:text-yellow-900 font-medium">
                          Click to view all captured text from label scan
                        </summary>
                        <div className="mt-2 text-xs text-gray-700 max-h-48 overflow-y-auto bg-white p-3 rounded border border-yellow-300 font-mono whitespace-pre-wrap break-words">
                          {pkg.raw_data}
                        </div>
                      </details>
                    </div>
                  )}

                  {/* Pickup Information */}
                  {pkg.is_picked_up && pkg.picked_up_by && (
                    <div className={`p-3 rounded-lg border ${
                      pkg.picked_up_by.type === '3rd_party' 
                        ? 'bg-purple-50 border-purple-200' 
                        : 'bg-green-50 border-green-200'
                    }`}>
                      <div className={`font-semibold text-sm mb-2 ${
                        pkg.picked_up_by.type === '3rd_party' 
                          ? 'text-purple-800' 
                          : 'text-green-800'
                      }`}>
                        <i className={`mr-2 ${
                          pkg.picked_up_by.type === '3rd_party' 
                            ? 'fas fa-user-friends' 
                            : 'fas fa-user-check'
                        }`}></i>
                        {pkg.picked_up_by.type === '3rd_party' ? '3rd Party Pickup' : 'Picked Up By'}
                      </div>
                      <div className={`space-y-1 text-sm ${
                        pkg.picked_up_by.type === '3rd_party' 
                          ? 'text-purple-900' 
                          : 'text-green-900'
                      }`}>
                        {pkg.picked_up_by.type === '3rd_party' && pkg.picked_up_by.original_recipient && (
                          <div className="mb-2 pb-2 border-b border-purple-200">
                            <strong>Original Recipient:</strong> {pkg.picked_up_by.original_recipient}
                          </div>
                        )}
                        {pkg.picked_up_by.name && (
                          <div>
                            <strong>{pkg.picked_up_by.type === '3rd_party' ? 'Picked Up By:' : 'Name:'}</strong> {pkg.picked_up_by.name}
                          </div>
                        )}
                        {pkg.picked_up_by.id && (
                          <div>
                            <strong>ID:</strong> {pkg.picked_up_by.id}
                          </div>
                        )}
                        {pkg.picked_up_by.phone && (
                          <div>
                            <strong>Phone:</strong> {pkg.picked_up_by.phone}
                          </div>
                        )}
                        {pkg.picked_up_by.email && (
                          <div>
                            <strong>Email:</strong> {pkg.picked_up_by.email}
                          </div>
                        )}
                        {pkg.pickup_date && (
                          <div className={`text-xs mt-2 ${
                            pkg.picked_up_by.type === '3rd_party' 
                              ? 'text-purple-700' 
                              : 'text-green-700'
                          }`}>
                            <i className="fas fa-clock mr-1"></i>
                            Picked up on: {formatDate(pkg.pickup_date)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                    {pkg.captured_image && (
                      <button
                        onClick={() => handleViewImage(pkg)}
                        className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-4 py-2 text-sm rounded-lg flex items-center space-x-2 hover:from-purple-600 hover:to-indigo-700 transition-all"
                      >
                        <i className="fas fa-image"></i>
                        <span>View Picture</span>
                      </button>
                    )}
                    
                    {!pkg.is_picked_up && (
                      <button
                        onClick={() => handlePickup(pkg.id, pkg)}
                        className="btn-success px-4 py-2 text-sm rounded-lg flex items-center space-x-2"
                      >
                        <i className="fas fa-check"></i>
                        <span>Mark as Picked Up</span>
                      </button>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEdit(pkg);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm rounded-lg flex items-center space-x-2 transition-all"
                      type="button"
                    >
                      <i className="fas fa-edit"></i>
                      <span>Edit</span>
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(pkg.id, pkg);
                      }}
                      className="btn-danger px-4 py-2 text-sm rounded-lg flex items-center space-x-2"
                      type="button"
                    >
                      <i className="fas fa-trash"></i>
                      <span>Delete</span>
                    </button>
                  </div>

                </div>
              </div>
            )}
            
          </div>
        );
      })}
      
      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" onClick={() => setShowImageModal(false)}>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-3xl"
            >
              <i className="fas fa-times-circle"></i>
            </button>
            
            {/* Package Info Header */}
            {selectedPackage && (
              <div className="bg-white rounded-t-xl p-4 mb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      Box #{selectedPackage.box_number} - {selectedPackage.recipient_name}
                    </h3>
                    {selectedPackage.tracking_numbers && selectedPackage.tracking_numbers.length > 0 && (
                      <p className="text-gray-600 font-mono">
                        {selectedPackage.tracking_numbers.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold">
                    <i className="fas fa-camera mr-2"></i>
                    Scanned Proof
                  </div>
                </div>
                
                {/* Show what was extracted */}
                {selectedPackage.ocr_data && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                    <div className="font-bold text-blue-800 text-sm mb-2">
                      <i className="fas fa-robot mr-2"></i>
                      Extracted Information:
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {selectedPackage.ocr_data.recipient_name && (
                        <div>
                          <span className="font-semibold text-gray-700">Name:</span>
                          <span className="ml-1 text-gray-600">{selectedPackage.ocr_data.recipient_name}</span>
                        </div>
                      )}
                      {selectedPackage.ocr_data.tracking_numbers && selectedPackage.ocr_data.tracking_numbers.length > 0 && (
                        <div>
                          <span className="font-semibold text-gray-700">Tracking:</span>
                          <span className="ml-1 text-gray-600">{selectedPackage.ocr_data.tracking_numbers.join(', ')}</span>
                        </div>
                      )}
                      {selectedPackage.ocr_data.phones && selectedPackage.ocr_data.phones.length > 0 && (
                        <div>
                          <span className="font-semibold text-gray-700">Phone:</span>
                          <span className="ml-1 text-gray-600">{selectedPackage.ocr_data.phones.join(', ')}</span>
                        </div>
                      )}
                      {selectedPackage.ocr_data.emails && selectedPackage.ocr_data.emails.length > 0 && (
                        <div>
                          <span className="font-semibold text-gray-700">Email:</span>
                          <span className="ml-1 text-gray-600">{selectedPackage.ocr_data.emails.join(', ')}</span>
                        </div>
                      )}
                      {selectedPackage.ocr_data.carrier && (
                        <div>
                          <span className="font-semibold text-gray-700">Carrier:</span>
                          <span className="ml-1 text-gray-600">{selectedPackage.ocr_data.carrier}</span>
                        </div>
                      )}
                      {selectedPackage.ocr_data.confidence_score && (
                        <div>
                          <span className="font-semibold text-gray-700">Confidence:</span>
                          <span className="ml-1 text-gray-600">{Math.round(selectedPackage.ocr_data.confidence_score * 100)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Captured Image */}
            <div className="bg-white rounded-b-xl p-4">
              <img 
                src={selectedImage} 
                alt="Captured package label" 
                className="w-full h-auto rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingPackage && (
        <div ref={editModalRef} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800">
                <i className="fas fa-edit mr-2 text-blue-600"></i>
                Edit Package #{editingPackage.box_number}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="fas fa-times text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* Recipient Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recipient Name *
                </label>
                <input
                  type="text"
                  value={editForm.recipient_name}
                  onChange={(e) => setEditForm({ ...editForm, recipient_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter recipient name"
                />
              </div>

              {/* Tracking Numbers */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tracking Numbers
                </label>
                {editForm.tracking_numbers.map((tracking, index) => (
                  <div key={index} className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={tracking}
                      onChange={(e) => handleTrackingNumberChange(index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Tracking #${index + 1}`}
                    />
                    <button
                      onClick={() => removeTrackingNumber(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
                <button
                  onClick={addTrackingNumber}
                  className="mt-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2"
                >
                  <i className="fas fa-plus"></i>
                  <span>Add Tracking Number</span>
                </button>
              </div>

              {/* Carrier */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Carrier
                </label>
                <select
                  value={editForm.carrier}
                  onChange={(e) => setEditForm({ ...editForm, carrier: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Carrier</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                  <option value="DHL">DHL</option>
                  <option value="Mondial">Mondial</option>
                  <option value="USPS">USPS</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter phone number"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter email address"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Add any additional notes"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all"
              >
                <i className="fas fa-save mr-2"></i>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageList;